import {
  ApiCipSipRecord,
  ApiProcessDetail,
  ChemicalRecord,
  CipSipFormValues,
  TransportRoute,
  WasteRecord
} from "../types/processDetail.types";
import { cipSipRecordId, mapCipSipFormValuesToApi } from "../utils/processDetail.utils";
import { useProcessChemicalCrud } from "./useProcessChemicalCrud";
import { useProcessWasteCrud } from "./useProcessWasteCrud";
import { ApiProcessingRecord } from "../../../../../types/prodcut-data-review/product-data.types";

interface UseProcessDetailActionsParams {
  chemicals: ChemicalRecord[];
  wastes: WasteRecord[];
  detail: ApiProcessDetail | undefined;
  chemicalCrud: ReturnType<typeof useProcessChemicalCrud>;
  wasteCrud: ReturnType<typeof useProcessWasteCrud>;
  onSaveCipSip: (updated: ApiProcessingRecord) => void;
}

/** Wires the CRUD hooks + CIP/SIP save-through-process-PUT into the (id, patch) shaped
 * callbacks `ProcessSubsections` expects — the CRUD hooks need the current full record
 * to merge a patch onto, but the child UI only ever hands back an id and a patch. */
export const useProcessDetailActions = ({
  chemicals,
  wastes,
  detail,
  chemicalCrud,
  wasteCrud,
  onSaveCipSip
}: UseProcessDetailActionsParams) => {
  const findChemical = (id: string): ChemicalRecord | undefined => chemicals.find(c => c.id === id);
  const findWaste = (id: string): WasteRecord | undefined => wastes.find(w => w.id === id);

  const currentCipSip: ApiCipSipRecord[] = detail?.cipSip ?? [];

  const saveCipSip = (nextCipSip: ApiCipSipRecord[]) => {
    if (!detail) return;
    onSaveCipSip({ ...detail, cipSip: nextCipSip });
  };

  const onAddCip = (values: CipSipFormValues) => {
    saveCipSip([...currentCipSip, mapCipSipFormValuesToApi(values)]);
  };

  const onUpdateCip = (id: string, patch: CipSipFormValues) => {
    saveCipSip(
      currentCipSip.map((rec, i) =>
        cipSipRecordId(rec, i) === id ? { ...mapCipSipFormValuesToApi(patch), _id: rec._id } : rec
      )
    );
  };

  const onRemoveCip = (id: string) => {
    saveCipSip(currentCipSip.filter((rec, i) => cipSipRecordId(rec, i) !== id));
  };

  const onAddRoute = (chemId: string, route: Pick<TransportRoute, "type" | "distance">) =>
    chemicalCrud.addRoute(chemId, route);

  const onUpdateRoute = (chemId: string, routeId: string, patch: Partial<TransportRoute>) => {
    const current = findChemical(chemId);
    const route = current?.routes.find(r => r.id === routeId);
    if (!route) return;
    chemicalCrud.updateRoute(routeId, { ...route, ...patch });
  };

  const onRemoveRoute = (chemId: string, routeId: string) => chemicalCrud.removeRoute(chemId, routeId);

  const onUpdateChemical = (id: string, patch: Partial<ChemicalRecord>) => {
    const current = findChemical(id);
    if (!current) return;
    chemicalCrud.updateChemical(id, patch, current);
  };

  const onUpdateWaste = (id: string, patch: Partial<WasteRecord>) => {
    const current = findWaste(id);
    if (!current) return;
    wasteCrud.updateWaste(id, patch, current);
  };

  return {
    onAddCip,
    onUpdateCip,
    onRemoveCip,
    onAddRoute,
    onUpdateRoute,
    onRemoveRoute,
    onUpdateChemical,
    onUpdateWaste
  };
};
